import { Box, Grid, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import SimpleSearchBar from '../../../common-components/simple-search-bar/simple-search-bar';
import VirtualisedListWrapper from '../../../common-components/virtualised-list/virtualised-list';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  MyFleetActionButtonGroupStatus,
  MyFleetVessel,
  MyFleetVessels,
  VesselOption,
} from '../../../models/my-fleet.model';

import { ProAlert } from '../../../models/pro.model';
import { setSearchFilterValue } from '../../../state/my-fleet/my-fleet-vessels.slice';
import MyFleetVesselDeletion from '../my-fleet-delete-vessels/my-fleet-delete-vessels';
import MyFleetVesselDownload from '../my-fleet-download-vessel-pro-report-pdf/my-fleet-pro-pdf-download';
import MyFleetVesselListItem from '../my-fleet-vessel-item/my-fleet-vessel-item';
import ProAlertDeleteDialog from '../my-fleet-vessel-item/pro-alert/delete-dialog/delete-dialog';

function MyFleetVesselsList({
  myFleetVessels,
  searchFilterValue,
  onDeleteSuccess,
}: {
  myFleetVessels: MyFleetVessels | null;
  searchFilterValue: string;
  onDeleteSuccess: () => void;
}) {
  const dispatch = useAppDispatch();
  const { proAlerts } = useAppSelector((state) => state.proAlerts);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [selectedImos, setSelectedImos] = useState<string[]>([]);
  const [selectedVessels, setSelectedVessels] = useState<MyFleetVessels | []>(
    []
  );

  const [activeVesselOption, setActiveVesselOption] = useState<{
    type: VesselOption | null;
    imo: string | null;
  }>({ type: null, imo: null });

  const [activeButtonGroup, setActiveButtonGroup] =
    useState<MyFleetActionButtonGroupStatus>(
      MyFleetActionButtonGroupStatus.Idle
    );

  const filteredVessels =
    myFleetVessels?.filter(
      (vessel) =>
        vessel.name?.toLowerCase().includes(searchFilterValue.toLowerCase()) ||
        vessel.imo.toLowerCase().includes(searchFilterValue.toLowerCase()) ||
        vessel.vessel_type
          ?.toLowerCase()
          .includes(searchFilterValue.toLowerCase())
    ) || [];

  const handleVesselSelection = (imo: string, checked: boolean) => {
    if (activeButtonGroup === MyFleetActionButtonGroupStatus.Download) {
      setSelectedImos(checked ? [imo] : []);
      setSelectedVessels(
        checked ? myFleetVessels?.filter((v) => v.imo === imo) || [] : []
      );
    } else {
      setSelectedImos((prev) =>
        checked ? [...prev, imo] : prev.filter((id) => id !== imo)
      );
      setSelectedVessels((prevSelected) => {
        if (checked) {
          const vessel = myFleetVessels?.find((v) => v.imo === imo);
          return vessel ? [...prevSelected, vessel] : prevSelected;
        }
        return prevSelected.filter((v) => v.imo !== imo);
      });
    }
  };

  const resetState = () => {
    setSelectedImos([]);
    setSelectedVessels([]);
    setSelectedAlertId(null);
  };

  const getProAlertForVessel = useCallback(
    (vesselId: string | undefined, alerts: ProAlert[] | null) => {
      if (!vesselId || !alerts) return null;
      return alerts.find((alert) => alert.vessel_id === vesselId) || null;
    },
    [proAlerts]
  );

  const renderVesselItem = (vessel: MyFleetVessel) => (
    <MyFleetVesselListItem
      vessel={vessel}
      onChange={handleVesselSelection}
      selectedImos={selectedImos}
      deleteActive={activeButtonGroup === MyFleetActionButtonGroupStatus.Delete}
      downloadActive={
        activeButtonGroup === MyFleetActionButtonGroupStatus.Download
      }
      setActiveVesselOption={setActiveVesselOption}
      setSelectedAlertId={setSelectedAlertId}
      activeVesselOption={activeVesselOption}
      proAlert={getProAlertForVessel(vessel.vessel_id, proAlerts)}
    />
  );

  const renderNoDataMessage = (message: JSX.Element) => (
    <Grid container>
      <Grid item xs={12} sx={{ textAlign: 'center' }}>
        {message}
      </Grid>
    </Grid>
  );

  const noDataMessage = () => {
    if (myFleetVessels === null || myFleetVessels.length === 0) {
      return renderNoDataMessage(
        <Typography data-testid="no-saved-vessels-message">
          No saved vessels to display
        </Typography>
      );
    }
    return renderNoDataMessage(
      <Typography data-testid="no-matching-vessels-message">
        No Matching Vessels
      </Typography>
    );
  };

  return myFleetVessels && myFleetVessels.length > 0 ? (
    <>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', padding: '12px 20px' }}
      >
        <SimpleSearchBar
          handleChange={(value: string) =>
            dispatch(setSearchFilterValue(value))
          }
          filterValue={searchFilterValue}
          placeholder="Search Vessels by IMO, Name, Type"
          dataTestId="vessel-search-input"
        />
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            marginTop: 0.5,
          }}
        >
          {activeButtonGroup !== MyFleetActionButtonGroupStatus.Download && (
            <MyFleetVesselDeletion
              myFleetVessels={myFleetVessels}
              selectedImos={selectedImos}
              setSelectedImos={setSelectedImos}
              deleteActive={
                activeButtonGroup === MyFleetActionButtonGroupStatus.Delete
              }
              resetState={resetState}
              setDeleteActive={setActiveButtonGroup}
              onDeleteSuccess={onDeleteSuccess}
            />
          )}
          {activeButtonGroup !== MyFleetActionButtonGroupStatus.Delete && (
            <MyFleetVesselDownload
              selectedVessel={selectedVessels[0]}
              resetState={resetState}
              downloadActive={
                activeButtonGroup === MyFleetActionButtonGroupStatus.Download
              }
              setDownloadActive={setActiveButtonGroup}
            />
          )}
          <ProAlertDeleteDialog
            open={!!selectedAlertId}
            alert_id={selectedAlertId}
            resetState={resetState}
          />
        </Box>
      </Box>
      {filteredVessels && filteredVessels.length > 0 ? (
        <VirtualisedListWrapper
          items={filteredVessels}
          listContent={(index: number) =>
            renderVesselItem(filteredVessels[index])
          }
          selectedItemIndex={null}
          dataTestId="my-fleet-vessel-list"
        />
      ) : (
        noDataMessage()
      )}
    </>
  ) : (
    noDataMessage()
  );
}

export default MyFleetVesselsList;
